import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence, useAnimation, useTransform } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { 
  Globe, 
  ArrowRight,
  ChevronDown,
  Users, Megaphone,
  BarChart, CalendarDays, Flag,
  Award,
  Building2,
  Handshake,
  Star,
  TrendingUp,
  Clock,
  Download,
  Mail,
  Phone,
  MapPin,
  Send,
  Crown,
  Target,
  Briefcase,
  GraduationCap,
  Calendar,
  Menu,
  X,
  Github,
  Linkedin,
  Twitter,
  Instagram,
  Sun,
  Moon,
  Code,
  Lightbulb,
  Heart,
  Zap,
  ChevronRight,
  Quote,
  CheckCircle,
  ArrowUpRight,
  Sparkles
} from "lucide-react";

import Shanti_logo from "../assets/images/Shanti_logo.jpg"; // Ensure this image exists in the specified path
import About1 from "../assets/images/Ray_speech.jpg"; // Ensure this image exists in the specified path
import ICAPrez from "../assets/images/IMG-20250819-WA0024.jpg"; // Ensure this image exists in the specified path
import Ray_logo from "../assets/images/Ray_logo.jpg"; // Ensure this image exists in the specified path
import Coop from "../assets/images/Y20_Africa.jpg"; // Ensure this image exists in the specified path
import Cowganic from "../assets/images/cowganic_logo.png"; // Ensure this image exists in the specified path
import YS_Y20 from "../assets/images/journey/YouthSummit_Y20.jpg"; // Ensure this image exists in the specified path
import LoadingScreen from "../components/Loading";
import HeroSection from "../components/Hero";
import VS_standing from "../assets/images/Vidhansabha_standing.jpg"; // Ensure this image exists in the specified path
import Kai_logo from "../assets/images/Kai_Logo_Raw.jpg"; // Ensure this image exists in the specified path
import ICAGCC from "../assets/images/ICA_GCC.jpg"; 
// Enhanced Floating Elements Component
const FloatingElements = ({ isDark }) => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Floating Shapes */}
      <motion.div
        animate={{
          y: [0, -20, 0],
          rotate: [0, 180, 360],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "linear"
        }}
        className="absolute top-1/4 left-1/4 w-2 h-2 bg-red-400/30 rounded-full"
      />
      <motion.div
        animate={{
          y: [0, 30, 0],
          x: [0, 20, 0],
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        className="absolute top-3/4 right-1/3 w-1 h-1 bg-blue-400/40 rounded-full"
      />
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.6, 0.3],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        className="absolute bottom-1/4 left-1/2 w-3 h-3 bg-gradient-to-r from-red-400 to-pink-400 rounded-full blur-sm"
      />
    </div>
  );
};

// About Section with Professional Images
const AboutSection = ({ isDark }) => {
  // Main hook for the section's visibility
  const { ref, inView } = useInView({ threshold: 0.3, triggerOnce: true });

  // Custom hook for number animation using useState instead of useAnimation
  const useCounter = (toValue) => {
    const [currentValue, setCurrentValue] = useState(0);
    const { ref: counterRef, inView: counterInView } = useInView({ threshold: 0.1, triggerOnce: true });
    
    useEffect(() => {
      if (counterInView) {
        let startTime = null;
        const duration = 2500; // 2.5 seconds
        
        const animate = (timestamp) => {
          if (!startTime) startTime = timestamp;
          const progress = Math.min((timestamp - startTime) / duration, 1);
          
          // Easing function (circOut)
          const easeOut = Math.sqrt(1 - Math.pow(progress - 1, 2));
          const current = toValue * easeOut;
          
          setCurrentValue(Math.round(current));
          
          if (progress < 1) {
            requestAnimationFrame(animate);
          }
        };
        
        requestAnimationFrame(animate);
      }
    }, [counterInView, toValue]);

    return { counterRef, currentValue };
  };

  const countriesCounter = useCounter(110);
  const yearsCounter = useCounter(130);

  return (
    <section id="about" ref={ref} className={`py-8 sm:py-12 md:py-16 lg:py-20 xl:py-24 ${isDark ? 'bg-black' : 'bg-white'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 sm:gap-8 md:gap-12 lg:gap-16 xl:gap-24 items-center">
          
          {/* Left Side: Image with Decorative Element */}
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, ease: "easeInOut" }}
            className="lg:col-span-2 relative w-full"
          >
            <div className="relative p-2 sm:p-4">
              <img
                src={About1}
                alt="Professional portrait"
                className="relative z-10 rounded-2xl sm:rounded-3xl w-full h-auto object-cover shadow-2xl"
              />
              {/* Decorative Border */}
              <div className={`absolute top-0 left-0 w-full h-full rounded-2xl sm:rounded-3xl border-2 ${isDark ? 'border-red-500/20' : 'border-red-200'} transform translate-x-4 translate-y-4 sm:translate-x-6 sm:translate-y-6 lg:translate-x-8 lg:translate-y-8`}></div>
            </div>
          </motion.div>

          {/* Right Side: Content */}
          <motion.div 
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeInOut" }}
            className="lg:col-span-3 space-y-10"
          >
            {/* Title */}
            <div className="relative px-4 sm:px-0">
              <h2 className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                About <span className="text-red-600">Me</span>
              </h2>
              <div className="absolute top-full left-4 sm:left-0 mt-2 h-1 w-12 sm:w-16 md:w-20 bg-gradient-to-r from-red-600 to-red-800 rounded-full"></div>
            </div>
            
            {/* Bio Text */}
            <div className={`text-sm sm:text-base md:text-lg leading-relaxed space-y-3 sm:space-y-4 px-4 sm:px-0 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              <p>
                I'm a youth leader passionate about creating spaces where young people can lead with purpose, courage, and collaboration. At 20, I'm grateful to serve as the first Indian in 130 years to be elected President of the International Cooperative Alliance Youth Committee — a role that allows me to represent youth voices from over 110 countries on the global stage.
              </p>
              <p>
                My journey began at age 14, driven by a curiosity for sustainable farming and a belief that meaningful change can start early. I founded <em className="text-red-500 font-medium">Cowganic</em> not just as a project, but as a statement — that young people, regardless of age or background, have the power to lead.
              </p>
              <p>
                Since then, I've continued working with global organizations to elevate youth-led action in areas like climate resilience, social justice, and cooperative leadership. Every role I've taken on has been rooted in a simple belief: that youth aren't just the leaders of tomorrow — we're leaders today.
              </p>
            </div>

            {/* Impact Areas */}
            <div className={`grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 md:gap-6 p-3 sm:p-4 md:p-6 rounded-xl mx-4 sm:mx-0 ${isDark ? 'bg-gray-900' : 'bg-slate-50'}`}>
              <div className="text-center">
                <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Global Cooperation</p>
                <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Building bridges across nations</p>
              </div>
              <div className="text-center">
                <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Climate Action</p>
                <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Sustainable solutions for tomorrow</p>
              </div>
              <div className="text-center">
                <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Youth Leadership</p>
                <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Empowering next generation</p>
              </div>
            </div>
            
            {/* Values Header */}
            <div className="mt-6 sm:mt-8 px-4 sm:px-0">
              <h3 className={`text-lg sm:text-xl font-semibold mb-3 sm:mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Values I Lead With</h3>
            </div>
            
            {/* Key Values */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 md:gap-x-8 md:gap-y-6 px-4 sm:px-0">
              {[
                  { emoji: "❤️", label: "Purpose Before Position" },
                  { emoji: "🌐", label: "Think Global, Act Local" },
                  { emoji: "💡", label: "Innovation with Intention" },
                  { emoji: "👥", label: "Empowering Youth, Together" }
              ].map((value) => (
                <div key={value.label} className="flex items-center space-x-3">
                  <span className="text-lg">{value.emoji}</span>
                  <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{value.label}</span>
                </div>
              ))}
            </div>

          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Enhanced Experience Section
const ExperienceSection = ({ isDark = false }) => {
  const { ref, inView } = useInView({ threshold: 0.2, triggerOnce: true });

  const experiences = [
    {
      id: "ica-president",
      title: "President International Cooperative Alliance Youth Committee and Youth Representative to ICA Board", 
      organization: "International Cooperative Alliance",
      period: "Jul 2025 - Present",
      location: "Manchester, England, United Kingdom",
      description: "First Asian in 130 years to be elected President of the ICA Youth Committee, representing youth voices across 110+ countries",
      image: ICAPrez,
      achievements: [
        "Leading cooperative youth initiatives across 110+ countries",
        "Representing youth on the Board of International Cooperative Alliance",
        "Spearheading global programs for policy advocacy and training",
        "Championing cooperative entrepreneurship and climate action"
      ],
      color: "red",
      icon: Crown
    },
    {
      id: "wcoopef-president",
      title: "Global President Youth Wing",
      organization: "World Cooperation Economic Forum",
      period: "Dec 2024 - Present", 
      location: "Global",
      description: "Leading global youth cooperation initiatives and economic development programs",
      image: Coop,
      achievements: [
        "Empowering Youth in Cooperatives globally",
        "Fostering Youth Entrepreneurship initiatives",
        "Building bridges between young leaders worldwide",
        "Contributing to UN ECOSOC Youth Forum discussions"
      ],
      color: "blue",
      icon: Globe
    },
    {
      id: "infrastructure-leader",
      title: "Infrastructure Director",
      organization: "Ray & Shanti Infrastructure",
      period: "Sep 2024 - Present",
      location: "Ahmedabad, Gujarat, India",
      description: "Leading sustainable infrastructure development projects with focus on innovation and environmental responsibility",
      image: About1,
      achievements: [
        "Overseeing major infrastructure projects",
        "Implementing sustainable development practices",
        "Building strategic partnerships and alliances",
        "Driving innovation in construction technology"
      ],
      color: "green",
      icon: Building2
    }
  ];

  const getColorClasses = (color) => {
    switch(color) {
      case 'red': return 'text-red-600 border-red-200 bg-red-100/50';
      case 'blue': return 'text-blue-600 border-blue-200 bg-blue-100/50';
      case 'green': return 'text-green-600 border-green-200 bg-green-100/50';
      default: return 'text-gray-600 border-gray-200 bg-gray-100/50';
    }
  };

  const getColorClassesDark = (color) => {
    switch(color) {
      case 'red': return 'text-red-400 border-red-900/30 bg-red-900/20';
      case 'blue': return 'text-blue-400 border-blue-900/30 bg-blue-900/20';
      case 'green': return 'text-green-400 border-green-900/30 bg-green-900/20';
      default: return 'text-gray-400 border-gray-700/30 bg-gray-800/20';
    }
  };

  return (
    <section 
      id="experience" 
      ref={ref} 
      className={`py-8 sm:py-12 md:py-16 lg:py-20 xl:py-24 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto text-center mb-8 sm:mb-12 lg:mb-16"
        >
          <div className={`inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-2xl mb-3 sm:mb-4 md:mb-6 ${
            isDark ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-600 shadow-sm'
          }`}>
            <Star className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7" />
          </div>
          <h2 className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-2 sm:mb-3 md:mb-4 px-4 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            Leadership Journey
          </h2>
          <p className={`text-sm sm:text-base md:text-lg lg:text-xl max-w-3xl mx-auto px-4 ${
            isDark ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Building bridges across nations, industries, and generations through visionary leadership.
          </p>
        </motion.div>

        {/* Experience Cards */}
        <div className="space-y-8 sm:space-y-12 md:space-y-16 lg:space-y-20">
          {experiences.map((exp, index) => {
            const isReversed = index % 2 !== 0; // Alternate layout for every second item
            return (
              <motion.div
                key={exp.id}
                initial={{ opacity: 0, x: isReversed ? 50 : -50 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: index * 0.2, duration: 0.6, ease: "easeOut" }}
                className="group"
              >
                <div className={`flex flex-col lg:flex-row gap-6 sm:gap-8 md:gap-10 lg:gap-12 items-center ${isReversed ? 'lg:flex-row-reverse' : ''}`}>
                  
                  {/* Image Section */}
                  <div className="lg:w-1/2 w-full">
                    <div className="relative aspect-[4/3] rounded-lg sm:rounded-xl md:rounded-2xl overflow-hidden shadow-lg group-hover:shadow-2xl transition-shadow duration-300">
                      <img
                        src={exp.image}
                        alt={exp.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-in-out"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className="lg:w-1/2 w-full">
                    <div className={`p-4 sm:p-5 md:p-6 lg:p-8 rounded-lg sm:rounded-xl md:rounded-2xl border transition-all duration-300 group-hover:-translate-y-1 ${
                      isDark 
                        ? 'border-gray-700 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-gray-700/20 to-gray-800/50' 
                        : 'border-gray-200 bg-white'
                    }`}>
                      <div className="flex justify-between items-start gap-3 mb-3 sm:mb-4">
                        <h3 className={`text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {exp.title}
                        </h3>
                        <div className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center border ${isDark ? getColorClassesDark(exp.color) : getColorClasses(exp.color)}`}>
                          <exp.icon className="w-5 h-5 sm:w-6 sm:h-6" />
                        </div>
                      </div>
                      
                      <p className={`text-base sm:text-lg md:text-xl font-semibold mb-3 sm:mb-4 md:mb-5 ${
                          exp.color === 'red' ? 'text-red-500' :
                          exp.color === 'blue' ? 'text-blue-500' :
                          exp.color === 'green' ? 'text-green-500' : 'text-gray-500'
                      }`}>
                          {exp.organization}
                      </p>

                      <div className={`flex flex-col sm:flex-row sm:items-center gap-x-4 md:gap-x-6 gap-y-2 text-xs sm:text-sm border-y py-2 sm:py-3 mb-4 sm:mb-5 md:mb-6 ${isDark ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-600'}`}>
                          <div className="flex items-center">
                              <Calendar className="w-3 h-3 sm:w-4 sm:h-4 mr-2" /> {exp.period}
                          </div>
                          <div className="flex items-center">
                              <MapPin className="w-3 h-3 sm:w-4 sm:h-4 mr-2" /> {exp.location}
                          </div>
                      </div>

                      <p className={`text-sm sm:text-base mb-4 sm:mb-5 md:mb-6 leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        {exp.description}
                      </p>

                      <div className="space-y-2 sm:space-y-3">
                        {exp.achievements.map((achievement) => (
                          <div key={achievement} className="flex items-start">
                            <div className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full mt-1.5 sm:mt-2 mr-3 sm:mr-4 flex-shrink-0 ${
                              exp.color === 'red' ? 'bg-red-500' :
                              exp.color === 'blue' ? 'bg-blue-500' :
                              exp.color === 'green' ? 'bg-green-500' : 'bg-gray-500'
                            }`}></div>
                            <span className={`text-sm sm:text-base leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                              {achievement}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )
          })}

        </div>
      </div>
    </section>
  );
};

// Initiative Row Component
const InitiativeRow = ({ initiative, userImage, isDark, theme, isReversed }) => {
  const { ref, inView } = useInView({ threshold: 0.3, triggerOnce: true });
  const currentTheme = theme[initiative.color];

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, ease: 'easeOut' }}
      className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center"
    >
      {/* Initiative Image */}
      <div className={`rounded-2xl overflow-hidden ${isReversed ? 'lg:order-last' : ''}`}>
        <img src={initiative.image} alt={initiative.title} className="w-full h-full object-cover aspect-[4/3]"/>
      </div>

      {/* Initiative Content */}
      <div className="flex flex-col">
        <div className="flex items-center gap-4 mb-4">
          <img src={userImage} alt="Your Photo" className="w-14 h-14 rounded-full border-2 border-white dark:border-gray-700 shadow-sm" />
          <div>
            <h3 className={`text-2xl lg:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {initiative.title}
            </h3>
            <p className={`text-lg font-medium ${isDark ? currentTheme.darkText : currentTheme.text}`}>
              {initiative.subtitle}
            </p>
          </div>
        </div>
        
        <p className={`mt-4 text-base leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
          {initiative.description}
        </p>
        
        <div className={`grid grid-cols-3 gap-4 text-center my-8 py-6 border-y ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          {Object.entries(initiative.stats).map(([key, value]) => (
            <div key={key}>
              <div className={`text-2xl font-bold ${isDark ? currentTheme.darkText : currentTheme.text}`}>{value}</div>
              <div className="text-xs capitalize text-gray-500 dark:text-gray-400">{key}</div>
            </div>
          ))}
        </div>

        <div>
          <h4 className={`text-sm font-semibold mb-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Key Focus Areas</h4>
          <div className="space-y-2">
            {initiative.goals.map(goal => (
              <div key={goal} className="flex items-center">
                <CheckCircle className={`w-5 h-5 mr-3 flex-shrink-0 ${isDark ? currentTheme.darkText : currentTheme.text}`} />
                <span className={`${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{goal}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Enhanced Initiatives Section
// const InitiativesSection = ({ isDark }) => {
//   // IMPORTANT: Add the URL to your personal photo here
//   const userImage = "https://images.unsplash.com/photo-1599566150163-29194dcaad36?q=80&w=300&h=300&auto=format&fit=crop";

//   const initiatives = [
//     {
//       id: "back2roots",
//       title: "Back2Roots",
//       subtitle: "Reconnecting with Our Heritage",
//       description: "A movement to help youth reconnect with their cultural roots, traditional values, and ancestral wisdom while building a bridge between heritage and modernity.",
//       image: "https://images.unsplash.com/photo-1500595046743-cd271d694d30?q=80&w=800&h=600&auto=format&fit=crop",
//       icon: TreePine,
//       color: "green",
//       stats: { Participants: "25K+", Events: "150+", Countries: "15" },
//       goals: ["Cultural Heritage Preservation", "Intergenerational Connection", "Sustainable Practices Revival"],
//     },
//     {
//       id: "back2community",
//       title: "Back2Community",
//       subtitle: "Strengthening Local Bonds",
//       description: "Empowering communities through collaborative initiatives, fostering local engagement, and building stronger social connections in our increasingly digital world.",
//       image: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?q=80&w=800&h=600&auto=format&fit=crop",
//       icon: Users,
//       color: "blue",
//       stats: { Communities: "500+", Volunteers: "10K+", Projects: "200+" },
//       goals: ["Community Engagement Boost", "Local Leadership Development", "Social Cohesion Building"],
//     },
//     {
//       id: "youth-entrepreneurship",
//       title: "Youth Entrepreneurship",
//       subtitle: "Fostering Innovation & Enterprise",
//       description: "Nurturing the next generation of entrepreneurs through mentorship, funding opportunities, and skill development programs that turn ideas into impactful ventures.",
//       image: "https://images.unsplash.com/photo-1559136555-9303baea8ebd?q=80&w=800&h=600&auto=format&fit=crop",
//       icon: Rocket,
//       color: "purple",
//       stats: { Startups: "300+", Funding: "$5M+", Mentors: "1K+" },
//       goals: ["Startup Incubation Support", "Business Skill Development", "Innovation Ecosystem Building"],
//     },
//   ];
  
//   const theme = {
//     green:   { text: 'text-green-500',   darkText: 'text-green-400' },
//     blue:    { text: 'text-blue-500',     darkText: 'text-blue-400' },
//     purple:  { text: 'text-purple-500',   darkText: 'text-purple-400' },
//     red:     { text: 'text-red-500',      darkText: 'text-red-400' },
//     orange:  { text: 'text-orange-500',   darkText: 'text-orange-400' },
//   };

//   const { ref, inView } = useInView({ threshold: 0.2, triggerOnce: true });

//   return (
//     <section id="initiatives" className={`py-24 sm:py-32 ${isDark ? 'bg-gray-900' : 'bg-white'}`}>
//       <div className="max-w-7xl mx-auto px-6 lg:px-8">
//         <motion.div
//           ref={ref}
//           initial={{ opacity: 0, y: 30 }}
//           animate={inView ? { opacity: 1, y: 0 } : {}}
//           transition={{ duration: 0.6 }}
//           className="text-center mb-20"
//         >
//           <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-6 ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
//             <Megaphone className={`w-7 h-7 text-red-500`} />
//           </div>
//           <h2 className={`text-4xl lg:text-5xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
//             Global Initiatives
//           </h2>
//           <p className={`mt-6 text-lg lg:text-xl max-w-3xl mx-auto ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
//             A showcase of key initiatives I have led, focusing on youth empowerment, community building, and sustainable growth.
//           </p>
//         </motion.div>

//         <div className="space-y-24">
//           {initiatives.map((initiative, index) => (
//             <InitiativeRow
//               key={initiative.id}
//               initiative={initiative}
//               userImage={userImage}
//               isDark={isDark}
//               theme={theme}
//               isReversed={index % 2 !== 0}
//             />
//           ))}
//         </div>
//       </div>
//     </section>
//   );
// };

// Enhanced Projects Section
const ProjectsSection = ({ isDark }) => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });

  const projects = [
    {
      id: 1,
      title: "Cowganic",
      category: "Sustainable Agriculture",
      description: "Revolutionary organic farming solutions founded at age 14, promoting eco-friendly products and sustainable practices worldwide",
      image: Cowganic,
      technologies: ["Organic Farming", "Sustainability", "Environmental Science", "Supply Chain"],
      status: "Active",
      year: "2019",
      impact: "1000+ Farmers",
      metrics: { users: "10K+", countries: "3", awards: "3" },
      website: "https://www.instagram.com/cowganic/"
    },
    {
      id: 2,
      title: "Shanti Herbals",
      category: "Healthcare Innovation",
      description: "Herbal solutions addressing health challenges, representing India at major international exhibitions and trade shows",
      image: Shanti_logo,
      technologies: ["Herbal Medicine", "Healthcare", "R&D", "International Trade"],
      status: "Growing",
      year: "2022",
      impact: "Healthcare Innovation",
      metrics: { users: "5K+", countries: "3", awards: "2" },
      website: "https://www.shantiherbals.com/"
    },
    {
      id: 3,
      title: "Ray Infrastructure",
      category: "Infrastructure Development",
      description: "Leading sustainable infrastructure projects with focus on innovative construction technology and environmental responsibility",
      image: Ray_logo,
      technologies: ["Construction Tech", "Project Management", "Sustainability", "Innovation"],
      status: "Active",
      year: "2024",
      impact: "Major Projects",
      metrics: { projects: "15000+", team: "350+" },
      website: "https://rayinfra.in/"
    },
    {
      id: 4,
      title: "Kaizenith Technologies LLP",
      category: "Technology Solutions",
      description: "Innovative technology solutions driving digital transformation and automation across various industries",
      image: Kai_logo,
      technologies: ["Software Development", "AI/ML", "Automation", "Digital Transformation"],
      status: "Active",
      year: "2025",
      impact: "Tech Innovation",
      metrics: { clients: "10+", solutions: "25+", team: "20+" },
      website: "https://kaizenithtechnologies.com"
    }
  ];

  const handleProjectClick = (website) => {
    window.open(website, '_blank', 'noopener,noreferrer');
  };

  return (
    <section id="projects" ref={ref} className={`py-8 sm:py-12 md:py-16 lg:py-20 ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-8 sm:mb-12 md:mb-16"
        >
          <h2 className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-3 sm:mb-4 md:mb-6 px-4 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            Featured <span className="text-red-600">Ventures</span>
          </h2>
          <p className={`text-sm sm:text-base md:text-lg lg:text-xl max-w-3xl mx-auto px-4 ${
            isDark ? 'text-gray-300' : 'text-gray-600'
          }`}>
            Innovative projects driving sustainable change and social impact across industries
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
          {projects.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ delay: index * 0.2, duration: 0.6 }}
              whileHover={{ y: -10 }}
              onClick={() => handleProjectClick(project.website)}
              className={`group relative overflow-hidden rounded-2xl sm:rounded-3xl cursor-pointer ${
                isDark ? 'bg-gray-900 border border-gray-700' : 'bg-white border border-gray-200'
              } shadow-xl hover:shadow-2xl transition-all duration-500`}
            >
              {/* Project Image */}
              <div className="relative overflow-hidden h-40 sm:h-48 md:h-56 lg:h-64">
                <img 
                  src={project.image} 
                  alt={project.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                
                {/* Status & Year Badges */}
                <div className="absolute top-2 sm:top-4 left-2 sm:left-4 flex flex-wrap gap-1 sm:gap-2">
                  <span className={`px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs font-bold ${
                    project.status === 'Active' 
                      ? 'bg-green-500 text-white' 
                      : 'bg-blue-500 text-white'
                  }`}>
                    {project.status}
                  </span>
                  <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-white/20 backdrop-blur-sm text-white rounded-full text-xs font-medium border border-white/30">
                    {project.year}
                  </span>
                </div>

                {/* Category */}
                <div className="absolute bottom-2 sm:bottom-4 left-2 sm:left-4">
                  <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-red-500/20 backdrop-blur-sm text-red-100 rounded-full text-xs sm:text-sm font-medium border border-red-400/30">
                    {project.category}
                  </span>
                </div>
              </div>

              {/* Project Content */}
              <div className="p-3 sm:p-4 md:p-6">
                <div className="flex items-center justify-between mb-2 sm:mb-3">
                  <h3 className={`text-base sm:text-lg md:text-xl font-bold ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}>
                    {project.title}
                  </h3>
                 
                </div>
                
                <p className={`text-xs sm:text-sm leading-relaxed mb-3 sm:mb-4 line-clamp-3 ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  {project.description}
                </p>

                {/* Technologies */}
                <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4">
                  {project.technologies.slice(0, 3).map((tech) => (
                    <span
                      key={tech}
                      className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-md text-xs ${
                        isDark 
                          ? 'bg-gray-800 text-gray-300 border border-gray-700' 
                          : 'bg-gray-100 text-gray-700 border border-gray-200'
                      }`}
                    >
                      {tech}
                    </span>
                  ))}
                  {project.technologies.length > 3 && (
                    <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-md text-xs ${
                      isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-600'
                    }`}>+{project.technologies.length - 3}</span>
                  )}
                </div>

                {/* Project Metrics */}
                <div className={`pt-3 sm:pt-4 border-t ${
                  isDark ? 'border-gray-700' : 'border-gray-200'
                }`}>
                  <div className="grid grid-cols-3 gap-1 sm:gap-2 text-center">
                    {Object.entries(project.metrics).map(([key, value]) => (
                      <div key={key}>
                        <div className={`text-xs sm:text-sm font-bold ${
                          isDark ? 'text-white' : 'text-gray-900'
                        }`}>
                          {value}
                        </div>
                        <div className={`text-xs capitalize ${
                          isDark ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          {key}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Image Gallery Section
const GallerySection = ({ isDark }) => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });

  const galleryImages = [
    {
      src: Coop,
      alt: "Y20 Summit - South Africa",
      category: "Leadership"
    },
    {
      src: YS_Y20,
      alt: "Youth Summit",
      category: "Events"
    },
    {
      src: VS_standing,
      alt: "Vidhan Sabha Speech",
      category: "Leadership"
    },

    {
      src: ICAGCC,
      alt: "International Cooperative Alliance Global Conference",
      category: "Leadership"
    }
  ];

  return (
    <section id="gallery" ref={ref} className={`py-8 sm:py-12 md:py-16 lg:py-20 ${isDark ? 'bg-gray-900' : 'bg-white'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-8 sm:mb-12 md:mb-16"
        >
          <h2 className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-3 sm:mb-4 md:mb-6 px-4 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            Visual <span className="text-red-600">Journey</span>
          </h2>
          <p className={`text-sm sm:text-base md:text-lg lg:text-xl max-w-3xl mx-auto px-4 ${
            isDark ? 'text-gray-300' : 'text-gray-600'
          }`}>
            Moments captured from conferences, projects, and global initiatives
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
          {galleryImages.map((image, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              whileHover={{ scale: 1.05 }}
              className={`group relative overflow-hidden rounded-xl sm:rounded-2xl ${
                index % 3 === 1 ? 'lg:row-span-2' : ''
              }`}
            >
              <img
                src={image.src}
                alt={image.alt}
                className={`w-full object-cover transition-transform duration-700 group-hover:scale-110 ${
                  index % 3 === 1 ? 'h-64 sm:h-80 lg:h-full' : 'h-48 sm:h-56 md:h-64'
                }`}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              
              <div className="absolute bottom-2 sm:bottom-4 left-2 sm:left-4 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300 opacity-0 group-hover:opacity-100">
                <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-red-500/20 backdrop-blur-sm text-white rounded-full text-xs sm:text-sm font-medium border border-red-400/30">
                  {image.category}
                </span>
                <h4 className="text-white text-sm sm:text-base font-semibold mt-1 sm:mt-2">{image.alt}</h4>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Enhanced Contact Section
const ContactSection = ({ isDark }) => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    console.log('Form submitted:', formData);
    // Add your form submission logic here
  };

  return (
    <section id="contact" ref={ref} className="py-8 sm:py-12 md:py-16 lg:py-20 bg-gradient-to-br from-red-900 via-red-800 to-red-900 relative overflow-hidden">
      <FloatingElements isDark={true} />
      
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?q=80&w=1920&auto=format&fit=crop"
          alt="Contact background"
          className="w-full h-full object-cover opacity-10"
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-8 sm:mb-12 md:mb-16"
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-3 sm:mb-4 md:mb-6 px-4">
            Let's Create <span className="text-red-300">Impact Together</span>
          </h2>
          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-red-100 max-w-3xl mx-auto px-4">
            Ready to collaborate on global initiatives? Let's discuss how we can work together to make a difference
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 md:gap-10 lg:gap-12">
          {/* Enhanced Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -40 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="bg-white/10 backdrop-blur-lg rounded-xl sm:rounded-2xl md:rounded-3xl p-4 sm:p-6 md:p-8 border border-white/20 shadow-2xl">
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-4 sm:mb-6 md:mb-8 flex items-center">
                <Heart className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 text-red-300" />
                Connect With Me
              </h3>
              
              <div className="space-y-3 sm:space-y-4 md:space-y-6">
                {[
                  { icon: Mail, label: "Email", value: "harshsanghani2004@gmail.com" },
                  { icon: Globe, label: "Website", value: "www.harshsanghani.com" },
                  { icon: MapPin, label: "Location", value: "Ahmedabad, Gujarat, India" },
                  { icon: Phone, label: "Availability", value: "Global Remote & Travel" }
                ].map((contact, index) => (
                  <motion.div 
                    key={contact.label}
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ delay: 0.4 + index * 0.1 }}
                    className="flex items-center space-x-3 sm:space-x-4 p-3 sm:p-4 rounded-lg sm:rounded-xl hover:bg-white/5 transition-colors group cursor-pointer"
                  >
                    <contact.icon className="w-5 h-5 sm:w-6 sm:h-6 text-red-300 group-hover:scale-110 transition-transform flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-white font-medium text-sm sm:text-base">{contact.label}</p>
                      <p className="text-red-200 text-xs sm:text-sm truncate">{contact.value}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Stats */}
              <div className="mt-4 sm:mt-6 md:mt-8 pt-4 sm:pt-6 md:pt-8 border-t border-white/20">
                <div className="grid grid-cols-3 gap-2 sm:gap-3 md:gap-4 text-center">
                  <div>
                    <div className="text-xl sm:text-2xl font-bold text-white">110+</div>
                    <div className="text-xs text-red-200">Countries</div>
                  </div>
                  <div>
                    <div className="text-xl sm:text-2xl font-bold text-white">24/7</div>
                    <div className="text-xs text-red-200">Available</div>
                  </div>
                  <div>
                    <div className="text-xl sm:text-2xl font-bold text-white">100%</div>
                    <div className="text-xs text-red-200">Committed</div>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="mt-4 sm:mt-6 md:mt-8 pt-4 sm:pt-6 md:pt-8 border-t border-white/20">
                <p className="text-white font-medium mb-3 sm:mb-4 text-sm sm:text-base">Follow the Journey</p>
                <div className="flex flex-wrap gap-3 sm:gap-4">
                  {[
                    { icon: Linkedin, href: "https://www.linkedin.com/in/harsh-sanghani" },
                    { icon: Twitter, href: "https://x.com/HarshSanghani99" },
                    { icon: Instagram, href: "https://www.instagram.com/harsh_mukeshbhai_sanghani" }
                  ].map((social, index) => (
                    <motion.a
                      key={index}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ scale: 1.1, y: -2 }}
                      className="p-2 sm:p-3 bg-white/10 backdrop-blur-sm rounded-full text-white/70 hover:text-white transition-colors border border-white/20"
                    >
                      <social.icon className="w-4 h-4 sm:w-5 sm:h-5" />
                    </motion.a>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Enhanced Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: 40 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <div className="bg-white/10 backdrop-blur-lg rounded-xl sm:rounded-2xl md:rounded-3xl p-4 sm:p-6 md:p-8 border border-white/20 shadow-2xl">
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-4 sm:mb-6 flex items-center">
                <Zap className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 text-yellow-400" />
                Start a Conversation
              </h3>
              
              <div className="space-y-3 sm:space-y-4 md:space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
                  <motion.input
                    whileFocus={{ scale: 1.02 }}
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-white/10 border border-white/20 rounded-lg sm:rounded-xl text-sm sm:text-base text-white placeholder-red-200 focus:outline-none focus:border-red-300 focus:bg-white/15 transition-all backdrop-blur-sm"
                  />
                  <motion.input
                    whileFocus={{ scale: 1.02 }}
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-white/10 border border-white/20 rounded-lg sm:rounded-xl text-sm sm:text-base text-white placeholder-red-200 focus:outline-none focus:border-red-300 focus:bg-white/15 transition-all backdrop-blur-sm"
                  />
                </div>
                
                <motion.input
                  whileFocus={{ scale: 1.02 }}
                  type="text"
                  name="subject"
                  placeholder="Subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-white/10 border border-white/20 rounded-lg sm:rounded-xl text-sm sm:text-base text-white placeholder-red-200 focus:outline-none focus:border-red-300 focus:bg-white/15 transition-all backdrop-blur-sm"
                />
                
                <motion.textarea
                  whileFocus={{ scale: 1.02 }}
                  name="message"
                  placeholder="Your Message"
                  rows="4"
                  value={formData.message}
                  onChange={handleInputChange}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-white/10 border border-white/20 rounded-lg sm:rounded-xl text-sm sm:text-base text-white placeholder-red-200 focus:outline-none focus:border-red-300 focus:bg-white/15 transition-all backdrop-blur-sm resize-none"
                ></motion.textarea>
                
                <motion.button
                  onClick={handleSubmit}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full py-3 sm:py-4 bg-gradient-to-r from-white to-gray-100 text-red-900 text-sm sm:text-base font-bold rounded-lg sm:rounded-xl hover:shadow-lg hover:shadow-white/25 transition-all duration-300 flex items-center justify-center group"
                >
                  <Send className="w-4 h-4 sm:w-5 sm:h-5 mr-2 group-hover:translate-x-1 transition-transform" />
                  Send Message
                  <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 ml-2 group-hover:animate-pulse" />
                </motion.button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Back to Top Button
const BackToTopButton = ({ isDark }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  const toggleVisibility = useCallback(() => {
    const scrolled = document.documentElement.scrollTop;
    const maxHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
    const progress = (scrolled / maxHeight) * 100;

    setScrollProgress(progress);
    setIsVisible(scrolled > 500);
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", toggleVisibility, { passive: true });
    return () => window.removeEventListener("scroll", toggleVisibility);
  }, [toggleVisibility]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.5 }}
          className="fixed bottom-8 right-8 z-50"
        >
          <motion.button
            onClick={scrollToTop}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="group relative w-16 h-16 bg-gradient-to-r from-red-500 to-red-700 rounded-2xl shadow-xl hover:shadow-red-500/25 transition-all duration-300"
          >
            <ChevronDown className="w-6 h-6 text-white transform rotate-180 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 group-hover:-translate-y-4 transition-transform" />
            
            {/* Enhanced Progress ring */}
            <svg className="absolute inset-0 w-16 h-16 transform -rotate-90">
              <circle
                cx="32"
                cy="32"
                r="28"
                stroke="rgba(255,255,255,0.2)"
                strokeWidth="3"
                fill="none"
              />
              <circle
                cx="32"
                cy="32"
                r="28"
                stroke="white"
                strokeWidth="3"
                fill="none"
                strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 28}`}
                strokeDashoffset={`${2 * Math.PI * 28 * (1 - scrollProgress / 100)}`}
                className="transition-all duration-300"
                style={{
                  filter: 'drop-shadow(0 0 8px rgba(255,255,255,0.4))'
                }}
              />
            </svg>
          </motion.button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Main Portfolio Component
const Portfolio = () => {
  const [isLoadingComplete, setIsLoadingComplete] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isDark, setIsDark] = useState(false);

  const handleLoadingComplete = () => {
    setIsLoadingComplete(true);
    setTimeout(() => setIsLoaded(true), 500);
  };

  const toggleTheme = () => {
    setIsDark(!isDark);
  };

  useEffect(() => {
    // Add smooth scrolling
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => {
      document.documentElement.style.scrollBehavior = 'auto';
    };
  }, []);

  return (
    <div className={`min-h-screen overflow-x-hidden transition-all duration-500 ${
      isDark ? 'bg-gray-900' : 'bg-white'
    }`}>
      {/* Loading Screen */}
      <LoadingScreen onLoadingComplete={handleLoadingComplete} isDark={isDark} />
      
      {/* Main Content */}
      {isLoadingComplete && (
        <motion.main 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <HeroSection isDark={isDark} />
          <AboutSection isDark={isDark} />
          <ExperienceSection isDark={isDark} />
          {/* <InitiativesSection isDark={isDark} /> */}
          <ProjectsSection isDark={isDark} />
          <GallerySection isDark={isDark} />
          <ContactSection isDark={isDark} />
        </motion.main>
      )}
    </div>
  );
};

export default Portfolio;